import type { PlayerSettings, WatermarkPosition } from './types';

export const DEFAULT_PLAYER_SETTINGS: Readonly<PlayerSettings> = {
  watermarkEnabled: true,
  watermarkText: 'DS {code} · {time}',
  watermarkMotion: 'moving',
  watermarkPosition: 'top-left',
  primaryColor: '#5EE6BD',
  captionTextColor: '#FFFFFF',
  captionBackgroundColor: '#000000',
  captionSize: 'medium',
};

export const CAPTION_SIZES: ReadonlyArray<{ value: PlayerSettings['captionSize']; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const WATERMARK_POSITIONS: ReadonlyArray<{ value: WatermarkPosition; label: string }> = [
  { value: 'top-left', label: 'Top left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'center-left', label: 'Center left' },
  { value: 'center-right', label: 'Center right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-right', label: 'Bottom right' },
];

export function validPlayerColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

export function captionFontSize(size: PlayerSettings['captionSize']): string {
  return size === 'small' ? '.82em' : size === 'large' ? '1.18em' : '1em';
}

export function captionHighlightColor(value: string): string {
  const normalized = validPlayerColor(value) ? value : DEFAULT_PLAYER_SETTINGS.captionBackgroundColor;
  const [red, green, blue] = rgb(normalized);
  return `rgba(${red}, ${green}, ${blue}, .78)`;
}

function rgb(value: string): [number, number, number] {
  return [Number.parseInt(value.slice(1, 3), 16), Number.parseInt(value.slice(3, 5), 16), Number.parseInt(value.slice(5, 7), 16)];
}

function luminance(value: readonly number[]): number {
  const channels = value.map((channel) => {
    const normalized = channel / 255;
    return normalized <= .04045 ? normalized / 12.92 : ((normalized + .055) / 1.055) ** 2.4;
  });
  return channels[0]! * .2126 + channels[1]! * .7152 + channels[2]! * .0722;
}

function contrast(left: readonly number[], right: readonly number[]): number {
  const brightest = Math.max(luminance(left), luminance(right));
  const darkest = Math.min(luminance(left), luminance(right));
  return (brightest + .05) / (darkest + .05);
}

function hex(value: readonly number[]): string {
  return `#${value.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

export function playerControlAccent(primaryColor: string): string {
  const normalized = validPlayerColor(primaryColor) ? primaryColor.toUpperCase() : DEFAULT_PLAYER_SETTINGS.primaryColor;
  const source = rgb(normalized);
  const controlBackground = [5, 6, 8] as const;
  if (contrast(source, controlBackground) >= 3) return normalized;
  for (let step = 1; step <= 20; step += 1) {
    const amount = step / 20;
    const lighter = source.map((channel) => channel + (255 - channel) * amount);
    if (contrast(lighter, controlBackground) >= 3) return hex(lighter);
  }
  return '#FFFFFF';
}
